import type { Interview } from "@/types";
import { InterviewCard } from "../card/InterviewCard";

const START_H = 0;
const END_H = 24;
const HOUR_PX = 64;
const HOURS = Array.from({ length: END_H - START_H }, (_, i) => START_H + i);

interface Props {
  weekStart: Date;
  interviews: Interview[];
  onInterviewClick: (iv: Interview) => void;
  onSlotClick: (date: Date) => void;
  onInterviewDrop: (id: number, newDate: Date) => void;
}

function fmtHour(h: number) {
  return new Date(0, 0, 0, h).toLocaleTimeString(undefined, { hour: "numeric" });
}

function positionOf(iv: Interview) {
  const d = new Date(iv.scheduled_at);
  const top = (d.getHours() - START_H + d.getMinutes() / 60) * HOUR_PX;
  return { top, height: Math.max(22, (iv.duration_minutes / 60) * HOUR_PX - 2) };
}

export function WeekView({ weekStart, interviews, onInterviewClick, onSlotClick, onInterviewDrop }: Props) {
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });
  const today = new Date().toDateString();
  const totalH = (END_H - START_H) * HOUR_PX;

  function makeDropHandler(dayIdx: number) {
    return (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const id = Number(e.dataTransfer.getData("interview-id"));
      if (!id) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const rawH = (e.clientY - rect.top) / HOUR_PX + START_H;
      const snappedMin = Math.round((rawH % 1) * 4) * 15;
      const d = new Date(days[dayIdx]);
      d.setHours(Math.floor(rawH), snappedMin, 0, 0);
      onInterviewDrop(id, d);
    };
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Sticky day-header row */}
      <div className="flex shrink-0 shadow-sm">
        <div className="w-14 shrink-0" />
        <div className="flex flex-1">
          {days.map((day, i) => {
            const isToday = day.toDateString() === today;
            return (
              <div key={i} className={`flex flex-1 min-w-[100px] h-8 items-center justify-center border-l border-border-card text-xs font-semibold
                ${isToday ? "bg-rausch/10 text-rausch" : "text-near-black"}`}>
                <span>{day.toLocaleDateString(undefined, { weekday: "short" })}</span>
                <span className={`ml-1 flex h-5 w-5 items-center justify-center rounded-full text-[11px]
                  ${isToday ? "bg-rausch text-white" : ""}`}>
                  {day.getDate()}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Scrollable time grid */}
      <div className="flex flex-1 overflow-auto min-h-0">
        {/* Time labels */}
        <div className="w-14 shrink-0">
          {HOURS.map((h) => (
            <div key={h} style={{ height: HOUR_PX }} className="relative border-b border-border-card/50">
              <span className="absolute -top-2 right-2 text-[10px] text-text-secondary">{fmtHour(h)}</span>
            </div>
          ))}
        </div>

        {/* Day columns */}
        <div className="flex flex-1" style={{ height: totalH }}>
          {days.map((day, dayIdx) => {
            const ds = new Date(day); ds.setHours(START_H, 0, 0, 0);
            const de = new Date(day); de.setHours(END_H, 0, 0, 0);
            const dayInterviews = interviews.filter((iv) => {
              const t = new Date(iv.scheduled_at).getTime();
              return t >= ds.getTime() && t < de.getTime();
            });

            return (
              <div
                key={dayIdx}
                className="relative flex-1 min-w-[100px] border-l border-border-card"
                style={{ height: totalH }}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                onDrop={makeDropHandler(dayIdx)}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const rawH = (e.clientY - rect.top) / HOUR_PX + START_H;
                  const d = new Date(days[dayIdx]);
                  d.setHours(Math.floor(rawH), Math.round((rawH % 1) * 4) * 15, 0, 0);
                  onSlotClick(d);
                }}
              >
                {HOURS.map((h) => (
                  <div key={h} style={{ top: (h - START_H) * HOUR_PX, height: HOUR_PX }}
                    className="absolute w-full border-b border-border-card/40" />
                ))}
                {dayInterviews.map((iv) => {
                  const { top, height } = positionOf(iv);
                  return (
                    <InterviewCard
                      key={iv.id}
                      interview={iv}
                      style={{ position: "absolute", top, left: 2, right: 2, height }}
                      onClick={(e) => { e.stopPropagation(); onInterviewClick(iv); }}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
